import { TagIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { Invoice } from '@/modules/billing/model';
import { formatPrice, getPlanTableLabel } from '@/modules/billing/utils';

interface InvoiceSummaryProps {
  upcomingInvoice: Invoice | undefined;
  balance: number | undefined;
}

export const InvoiceSummary = ({ upcomingInvoice, balance }: InvoiceSummaryProps) => {
  const {
    tax,
    subtotal_excluding_tax,
    total_discount_amounts,
    discount,
    total,
    amount_due,
    default_tax_rates,
    currency = 'USD',
  } = upcomingInvoice ?? {};
  const { coupon } = discount ?? {};

  const { t } = useTranslation();

  const couponText = coupon?.amount_off
    ? formatPrice(coupon.amount_off, currency)
    : `${coupon?.percent_off}%`;

  const couponDiscount = total_discount_amounts?.[0]?.amount ?? 0;
  const { percentage, display_name, jurisdiction } = default_tax_rates?.[0] ?? {};
  const taxMessage = default_tax_rates?.[0]?.percentage
    ? `${display_name} ${jurisdiction} ${percentage}%`
    : 0;

  const getBalance = () => {
    if (-balance > total && amount_due === 0) {
      return -total;
    }
    return balance;
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border px-4 py-3">
      {subtotal_excluding_tax !== amount_due && (
        <div className="flex items-center justify-between">
          <p className="text-sm leading-none font-medium text-muted-foreground">
            {t('billing.subtotal')}
          </p>
          <p className="text-right font-mono text-sm leading-5 text-muted-foreground">
            {formatPrice(subtotal_excluding_tax, currency)}
          </p>
        </div>
      )}
      {coupon && (
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-sm leading-none font-medium text-muted-foreground">
            <TagIcon size={16} />
            {coupon.name} {getPlanTableLabel(coupon.object)}
            <span className="text-muted-foreground">
              ({couponText} {t('billing.off')})
            </span>
          </span>
          <p className="text-right font-mono text-sm leading-5 text-muted-foreground">
            - {formatPrice(couponDiscount, currency)}
          </p>
        </div>
      )}
      {!!tax && (
        <div className="flex items-center justify-between">
          <p className="text-sm leading-none font-medium text-muted-foreground">{taxMessage}</p>
          <p className="text-right font-mono text-sm leading-5 text-muted-foreground">
            {formatPrice(tax, currency)}
          </p>
        </div>
      )}
      {!!balance && (
        <div className="flex items-center justify-between">
          <p className="text-sm leading-none font-medium text-muted-foreground">
            {t('billing.balance')}
          </p>
          <p className="text-right font-mono text-sm leading-5 text-muted-foreground">
            {formatPrice(getBalance(), currency)}
          </p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <p className="text-sm leading-none font-semibold">{t('billing.table.total')}</p>
        <p className="text-right font-mono text-sm leading-5 font-semibold">
          {formatPrice(amount_due, currency)}
        </p>
      </div>
    </div>
  );
};
